import { isolateContent, t, useLanguage } from '@/lib/i18n'
import { useQuery } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/auth-context'
import { libraryPath } from '@/features/exams/library-params'
import { presentTestCard, testCardCopy } from '@/features/exams/test-card-presenter'
import { Skeleton } from '@/components/r5/Skeleton'
import { Content } from '@/components/ui/Bidi'
import { testsApi } from '@/lib/api'
import type { TestListParams } from '@/lib/types'

const SEARCH_DELAY_MS = 350
const SUGGESTION_LIMIT = 8

/**
 * Global typeahead over the practice library (GAP-6), available from every
 * student page.
 *
 * `GET /mocktest/tests/?search=…` is real: `_fetch_tests_list_impl` runs
 * `apply_tests_text_search` on the `search` parameter and already restricts
 * students to `is_active=True`. The presenter decides whether a row can be
 * opened, so a test that is expired or outside the student's plan appears with
 * an honest reason instead of vanishing from the suggestions — the same rule
 * the library page follows, for the same reason: a result the server counted
 * must not be silently hidden.
 */
export function HeaderSearch() {
  useLanguage()
  const { demo } = useAuth()
  const navigate = useNavigate()
  const listId = useId()
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const root = useRef<HTMLDivElement | null>(null)
  const input = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(term.trim()), SEARCH_DELAY_MS)
    return () => window.clearTimeout(timeout)
  }, [term])

  // A pointer press outside collapses the list; the input keeps its text so a
  // mis-click does not throw away what was typed.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  const params: TestListParams = { page: 1, limit: SUGGESTION_LIMIT, search: debounced }
  const query = useQuery({
    queryKey: ['tests', 'library', params],
    queryFn: () => testsApi.list(params),
    // Never in the sample journal, and never on an empty term — an unfiltered
    // first page is not a suggestion, it is noise.
    enabled: !demo && debounced.length > 1,
  })

  const suggestions = (query.data?.tests ?? []).map((test) => presentTestCard(test))
  const showList = open && debounced.length > 1

  const go = (index: number) => {
    const chosen = suggestions[index]
    if (!chosen) return
    setOpen(false)
    setTerm('')
    // A row the student cannot start goes to the library, where the reason is
    // stated, rather than to a runner route that would refuse them.
    navigate(chosen.structure.availability === 'startable' ? chosen.startPath : libraryPath({ search: chosen.title }))
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false)
      setActive(-1)
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!suggestions.length) return
      event.preventDefault()
      setOpen(true)
      setActive((current) => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1
        if (next < 0) return suggestions.length - 1
        if (next >= suggestions.length) return 0
        return next
      })
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      if (active >= 0 && suggestions[active]) go(active)
      // Enter with nothing highlighted is "search the library for this", which
      // is the surface that can page, filter and explain an empty result.
      else if (debounced) {
        setOpen(false)
        navigate(libraryPath({ search: debounced }))
      }
    }
  }

  if (demo) return null

  return (
    <div className="header-search" ref={root}>
      <div className="header-search__field">
        <Search size={16} aria-hidden="true" />
        {/* ARIA 1.2: the combobox role belongs on the input itself, not on a
            wrapper — a wrapper combobox hides the text field from the
            accessibility tree and the listbox stops being announced. */}
        <input
          ref={input}
          type="search"
          value={term}
          placeholder={t("Search practice tests")}
          aria-label={t("Search practice tests")}
          role="combobox"
          aria-expanded={showList && suggestions.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 && suggestions[active] ? `${listId}-${active}` : undefined}
          onChange={(event) => { setTerm(event.target.value); setOpen(true); setActive(-1) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query.isFetching && <Skeleton variant="line" width={28} className="header-search__busy" />}
        {term && (
          <button type="button" aria-label={t("Clear search")} onClick={() => { setTerm(''); setActive(-1); input.current?.focus() }}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* A listbox may contain only options, so the loading/empty/failure copy
          lives in its own live region beside it rather than as fake rows. */}
      {showList && !suggestions.length && (
        <div className="header-search__results header-search__note" role="status">
          {query.isError
            ? t("Search is unavailable right now. Try again in a moment.")
            : query.isPending ? t("Searching\u2026") : t("No practice tests match \u201c{0}\u201d.", { "0": isolateContent(debounced) })}
        </div>
      )}

      {showList && suggestions.length > 0 && (
        <ul className="header-search__results" id={listId} role="listbox" aria-label={t("Practice test suggestions")}>
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={index === active ? 'active' : undefined}
              onPointerDown={(event) => { event.preventDefault(); go(index) }}
              onMouseEnter={() => setActive(index)}
            >
              <strong>{suggestion.titleIsFallback ? testCardCopy(suggestion).title : <Content>{suggestion.title}</Content>}</strong>
              <small>
                {[
                  ...testCardCopy(suggestion).classification,
                  ...(suggestion.structure.availability === 'startable' ? [] : [testCardCopy(suggestion).cta]),
                ].join(' \u00b7 ')}
              </small>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
